import { Component, EventEmitter, Injector, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { SharedUiFormControlsModule } from '@snelstart/ui-form-controls';
import * as JSZip from 'jszip';
import { ButtonConfiguration, SharedUiButtonModule } from '@snelstart/ui-button';
import { SharedUiDialogModule } from '@snelstart/ui-dialog';
import { SharedUiDetailsModule } from '@snelstart/ui-details';
import {
    Aangifte,
    AanslagBtw,
    AanslagVpb,
    AanslagZwv,
    BerichtSbt,
    BokkzClient,
    BtwAangifte,
    ClientEntity,
    DossierItem,
    DossierItemEntity,
    IntermediaryEntity,
    Kind,
    PdfRequest,
    PdfRequestOptie,
    PdfRequestSoort,
    PdfRequestType,
    Toeslagen,
    Voorheffingen,
    VpbAangifte,
} from '../services/api.bokkz.services';
import { BerichtenService, OphaalResponse } from '../services/rekenclient.api';
import { ToastService } from '@snelstart/ui-toast';
import { BokkzAlgDataService } from '../services/bokkz-alg-data-service';
import { Observable, Subject, takeUntil } from 'rxjs';
import { Bezwaarschrift } from '../services/api.bokkz.bezwaar';
import { PmClient, RenderRequest } from '../services/snelstart.user.api';
import { Jaarrapportage } from '../services/api.bokkz.jaarstukken';
import { SharedUiAlertModule } from '@snelstart/ui-alert';
import { ActivatedRoute } from '@angular/router';
import { dispatchSavedItemEvent } from '@snelstart/domain-logic';

@Component({
    selector: 'snelstart-venture-fiscaal-domain-logic-workflow',
    standalone: true,
    imports: [SharedUiButtonModule, SharedUiDialogModule, SharedUiDetailsModule, SharedUiFormControlsModule, SharedUiAlertModule],
    templateUrl: './workflow.component.html',
    styleUrl: './workflow.component.css',
})
export class WorkflowComponent implements OnInit, OnDestroy {
    @Input() public id: any;
    @Input() public type: any;
    @Input() public dialogVisible = false;
    @Output() public dialogVisibleChange = new EventEmitter<boolean>();

    private destroy$ = new Subject<void>();
    public dossierItemFg = {} as FormGroup;
    public clientId = {} as string;
    public client = {} as ClientEntity;
    public intermediary = {} as IntermediaryEntity;
    public isZipTooLarge = false;
    public zipErrorMessage = '';

    constructor(
        private injector: Injector,
        private fb: FormBuilder,
        private readonly toastService: ToastService,
        private algDataService: BokkzAlgDataService,
        private activatedRoute: ActivatedRoute,
        private pmClient: PmClient,
    ) {
        this.dossierItemFg = this.fb.group<DossierItemEntity>({
            id: null as unknown as number,
            title: null as unknown as string,
            creator: null as unknown as string,
            dateCreation: null as unknown as Date,
            dateDeadline: null as unknown as Date,
            hasDeadline: null as unknown as boolean,
            isChecked: null as unknown as boolean,
            opmerkingen: null as unknown as string,
            attachment: null as unknown as string[] | null as unknown as boolean,
            nameAttachment: null as unknown as string,
            checked: null as unknown as boolean,
            type: null as unknown as string,
            year: null as unknown as string,
        });
    }

    config() {
        return ButtonConfiguration.create({
            onClick: () =>
                new Observable((subscription) => {
                    setTimeout(() => {
                        this.saveForm();
                        subscription.next(true);
                        subscription.complete();
                    }, 200);
                }),
        });
    }

    ngOnInit(): void {
        this.activatedRoute.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
            this.clientId = params['clientId'];
        });
        this.algDataService.selectedService.pipe(takeUntil(this.destroy$)).subscribe((value) => {
            this.dossierItemFg.get('type')?.setValue(String(value));
        });
        this.algDataService.selectedYear.pipe(takeUntil(this.destroy$)).subscribe((value) => {
            this.dossierItemFg.get('year')?.setValue(String(value));
        });
        this.algDataService.intermediary.pipe(takeUntil(this.destroy$)).subscribe((value: IntermediaryEntity) => {
            this.intermediary = value;
        });
        this.algDataService.client.pipe(takeUntil(this.destroy$)).subscribe((value: ClientEntity) => {
            this.client = value;
            this.dossierItemFg.get('clientId')?.setValue(String(value));
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    saveForm(): void {
        const bokkzClient = this.injector.get(BokkzClient);
        bokkzClient.postDossierItem(this.clientId, this.dossierItemFg.getRawValue()).subscribe((dossierItemEntity) => {
            if (dossierItemEntity) {
                const dossierItem: DossierItem = {
                    id: dossierItemEntity.id !== undefined ? String(dossierItemEntity.id) : undefined,
                    type: dossierItemEntity.type,
                    jaar: dossierItemEntity.year,
                    actie: dossierItemEntity.title,
                    soort: dossierItemEntity.creator,
                    open: dossierItemEntity.dateCreation,
                    deadline: dossierItemEntity.dateDeadline,
                    attachment: this.dossierItemFg.getRawValue().attachment !== undefined,
                };
                dispatchSavedItemEvent({identifier: String(dossierItem.id!),result: dossierItem});
                this.toastService.showSuccessMessage('Het dossier item is succesvol opgeslagen.');
            } else {
                this.toastService.showWarningMessage('Het dossier item is niet succesvol omgezet.');
            }
        });
        this.dialogVisibleChange.emit(false);
    }

    async readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (event) => {
                if (event.target) {
                    resolve(event.target.result as ArrayBuffer);
                } else {
                    reject(new Error('Failed to read file as ArrayBuffer'));
                }
            };
            reader.onerror = (event) => {
                reject(new Error('Failed to read file: ' + event.target?.error));
            };
            reader.readAsArrayBuffer(file);
        });
    }

    onFileSelected(event: Event) {
        const formData = this.dossierItemFg;
        if (event.target) {
            const target = event.target as HTMLInputElement;
            if (target.files) {
                const file: File = target.files[0];
                const zip = new JSZip();
                (async () => {
                    // Read the file as an ArrayBuffer
                    const arrayBuffer = await this.readFileAsArrayBuffer(file);
                    zip.file(file.name, arrayBuffer);

                    try {
                        // Generate the ZIP archive as a base64-encoded string
                        const zipContent = await zip.generateAsync({ type: 'base64' });

                        const zipSizeInBytes = (zipContent.length * 3) / 4; // Base64 encoding increases size by ~33%
                        const sizeInMB = zipSizeInBytes / (1024 * 1024);

                        console.log('ZIP size (MB):', sizeInMB.toFixed(2));

                        if (sizeInMB > 5) {
                            console.error('The ZIP file exceeds 5 MB!');
                            this.isZipTooLarge = true; // Example: Use this in your template to disable a button
                        } else {
                            this.isZipTooLarge = false;
                            formData.patchValue({ attachment: JSON.stringify(zipContent) });
                        }
                    } catch (error) {
                        console.error('Error generating ZIP archive:', error);
                    }
                })();
            }
        }
    }

    toonPdf() {
        const bokkzClient = this.injector.get(BokkzClient);
        const berichtenService = this.injector.get(BerichtenService);
        const attachment = this.dossierItemFg.value.attachment;
        const title = this.dossierItemFg.value.title as string;
        const tijd = this.dossierItemFg.getRawValue().dateCreation;
        const blob = this.dataURItoBlob(attachment);
        // let utf8Encode = new TextEncoder();
        const zip = new JSZip();
        zip.loadAsync(blob).then((zipFile) => {
            zipFile.forEach((relativePath, zipEntry) => {
                zipEntry.async('blob').then((fileBlob) => {
                    const fileBlobArrayObservable = fileBlob.text();
                    fileBlobArrayObservable.then((fileArray) => {
                        if (fileArray.includes('xbrl') && !fileArray.includes('bezwaarSpecificatie')) {
                            const ophaal = this.berichtSoortBepaler(fileArray);
                            berichtenService.converteerXbrlToJson(ophaal).subscribe((data) => {
                                const pdfRequest = this.pdfSbaBepaler(data, ophaal);
                                const pdfAntwoord = bokkzClient.getPdfRapport(pdfRequest);
                                pdfAntwoord.subscribe((pdfStringBase64) => {
                                    const blob = this.dataURItoBlob(pdfStringBase64);
                                    const url = URL.createObjectURL(blob);
                                    const link = document.createElement('a');
                                    const filenameWithoutExtension = relativePath.replace(/\.[^/.]+$/, '');
                                    const timestamp = new Date().toISOString().replace(/:/g, '_').replace(/\./g, '_');
                                    link.download = `${filenameWithoutExtension}_DownloadDate_${timestamp}.pdf`;
                                    link.href = url;
                                    document.body.appendChild(link);
                                    link.click();
                                });
                            });
                        } else if (this.isJsonObject(fileArray) && title && title.toLowerCase().includes('jaarrapportage')) {
                            const jaarRapportage = JSON.parse(fileArray) as Jaarrapportage;
                            const render = {
                                requestedReport: 'KleinPubs',
                                licensee: '12345678',
                                preparedBy: this.intermediary.officeName,
                                copies: 3,
                                outputFormat: 'PDF',
                                outputLanguage: 'nl',
                                stylesheet: 'PMReport',
                                debugOptions: {
                                    showElementStyles: false,
                                    showSbrId: false,
                                    showNotInKvK: true,
                                    showJrElement: false,
                                    showMappingNotFound: false,
                                },
                                financialStatement: jaarRapportage,
                            } as RenderRequest;

                            this.pmClient.postPmRenderRequestPublicatiestukken(render).subscribe({
                                next: (response) => {
                                    response.base64File;
                                    const blob = this.dataURItoBlob(response.base64File);
                                    const url = URL.createObjectURL(blob);
                                    const link = document.createElement('a');
                                    link.download =
                                        'Publicatiestukken ' +
                                        jaarRapportage.informatieRapportage?.financialReportingPeriodCurrentEndDate?.value +
                                        '-' +
                                        this.client.achternaam +
                                        '.pdf';
                                    link.href = url;
                                    document.body.appendChild(link);
                                    link.click();
                                },
                                error: (err) => {
                                    console.error('Failed to generate PDF:', err);
                                },
                            });
                        } else if (this.isJsonObject(fileArray) && title) {
                            const pdfRequest = this.pdfAangifteBepaler(title, fileArray, tijd);
                            const pdfAntwoord = bokkzClient.getPdfRapport(pdfRequest);
                            pdfAntwoord.subscribe((pdfStringBase64) => {
                                const blob = this.dataURItoBlob(pdfStringBase64);
                                const url = URL.createObjectURL(blob);
                                const link = document.createElement('a');

                                if (title.toLowerCase().includes('aangifte inkomstenbelasting')) {
                                    link.download = 'Aangifte_IB_' + this.client.achternaam + '_' + this.extractSubstring(relativePath);
                                }
                                if (title.toLowerCase().includes('opgave icp')) {
                                    link.download = relativePath.replace(/^BTW_Q_/, '');
                                } else {
                                    link.download = relativePath;
                                }
                                link.href = url;
                                document.body.appendChild(link);
                                link.click();
                            });
                        } else {
                            console.log('Processing pdf Onbekend');
                            console.log(fileArray);
                            const downloadLink = document.createElement('a');
                            downloadLink.href = URL.createObjectURL(fileBlob);
                            downloadLink.download = relativePath;
                            document.body.appendChild(downloadLink);
                            downloadLink.click();
                            document.body.removeChild(downloadLink);
                        }
                    });
                });
            });
        });
    }

    extractSubstring(input: string): string | null {
        const match = input.match(/ingediend.*/);
        return match ? match[0] : null;
    }

    dataURItoBlob(dataURI: string) {
        if (dataURI != null) {
            const byteString = atob(dataURI.replace(/"/g, ''));
            const arrayBuffer = new ArrayBuffer(byteString.length);
            const int8Array = new Uint8Array(arrayBuffer);
            for (let i = 0; i < byteString.length; i++) {
                int8Array[i] = byteString.charCodeAt(i);
            }
            return new Blob([int8Array], { type: 'application/zip' });
        } else throw 'No dataobject';
    }

    berichtSoortBepaler(xbrl: string): OphaalResponse {
        let ophaalResponse = {} as OphaalResponse;
        if (xbrl.includes('bd-rpt-ih-sba')) {
            ophaalResponse = {
                inhoud: btoa(xbrl),
                berichtsoort: 'IB',
            };
        } else if (xbrl.includes('bd-rpt-zvw-sba')) {
            ophaalResponse = {
                inhoud: btoa(xbrl),
                berichtsoort: 'ZVW',
            };
        } else if (xbrl.includes('bd-rpt-vpb-sba')) {
            ophaalResponse = {
                inhoud: btoa(xbrl),
                berichtsoort: 'VPB',
            };
        } else if (xbrl.includes('bd-rpt-tsl-sbt')) {
            ophaalResponse = {
                inhoud: btoa(xbrl),
                berichtsoort: 'TSG',
            };
        } else if (xbrl.includes('bd-rpt-ihz-via')) {
            ophaalResponse = {
                inhoud: btoa(xbrl),
                berichtsoort: 'VIA',
            };
        } else if (xbrl.includes('bd-rpt-ob-sba')) {
            ophaalResponse = {
                inhoud: btoa(xbrl),
                berichtsoort: 'OB',
            };
        } else if (xbrl.includes('uitstel-sba')) {
            ophaalResponse = {
                inhoud: btoa(xbrl),
                berichtsoort: 'BECON',
            };
        }
        return ophaalResponse;
    }

    pdfSbaBepaler(data: Aangifte | Toeslagen | VpbAangifte | BtwAangifte, ophaal: OphaalResponse): PdfRequest {
        const pdfRequest = {} as PdfRequest;
        if (ophaal.berichtsoort.includes('IB')) {
            pdfRequest.aanslagIbJaar1 = data;
            pdfRequest.concept = false;
            pdfRequest.soort = PdfRequestSoort.Aanslag;
            pdfRequest.optie = PdfRequestOptie.GeheleRapport;
            pdfRequest.type = PdfRequestType.IB;
        } else if (ophaal.berichtsoort.includes('ZVW')) {
            pdfRequest.aanslagZvwJaar1 = data as AanslagZwv;
            pdfRequest.concept = false;
            pdfRequest.soort = PdfRequestSoort.Aanslag;
            pdfRequest.optie = PdfRequestOptie.GeheleRapport;
            pdfRequest.type = PdfRequestType.ZVW;
        } else if (ophaal.berichtsoort.includes('VPB')) {
            pdfRequest.aanslagVpbJaar1 = data as AanslagVpb;
            pdfRequest.concept = false;
            pdfRequest.soort = PdfRequestSoort.Aanslag;
            pdfRequest.optie = PdfRequestOptie.GeheleRapport;
            pdfRequest.type = PdfRequestType.VPB;
        } else if (ophaal.berichtsoort.includes('TSG')) {
            pdfRequest.toeslagenJaar1 = data as BerichtSbt;
            pdfRequest.concept = false;
            pdfRequest.soort = PdfRequestSoort.Beschikking;
            pdfRequest.optie = PdfRequestOptie.GeheleRapport;
            pdfRequest.type = PdfRequestType.TSL;
        } else if (ophaal.berichtsoort.includes('OB')) {
            pdfRequest.aanslagBtwJaar1 = data as AanslagBtw;
            pdfRequest.concept = false;
            pdfRequest.soort = PdfRequestSoort.Aanslag;
            pdfRequest.optie = PdfRequestOptie.GeheleRapport;
            pdfRequest.type = PdfRequestType.BTW;
        } else if (ophaal.berichtsoort.includes('VIA')) {
            pdfRequest.aangifteJaar1 = data as Aangifte;
            pdfRequest.concept = false;
            pdfRequest.soort = PdfRequestSoort.Via;
            pdfRequest.optie = PdfRequestOptie.GeheleRapport;
            pdfRequest.type = PdfRequestType.IB;
        } else if (ophaal.berichtsoort.includes('BECON')) {
            pdfRequest.aanslagZvwJaar1 = data as AanslagZwv;
            pdfRequest.concept = false;
        }
        return pdfRequest;
    }

    pdfAangifteBepaler(title: string, jsondata: string, tijd?: Date): PdfRequest {
        const pdfRequest = {} as PdfRequest;
        if (title.toLowerCase().includes('bevestiging indienen aangifte inkomstenbelasting')) {
            pdfRequest.aangifteJaar1 = this.bepaalAangever(jsondata);
            pdfRequest.concept = false;
            pdfRequest.soort = PdfRequestSoort.Aangifte;
            pdfRequest.optie = PdfRequestOptie.GeheleRapport;
            pdfRequest.type = PdfRequestType.IB;
            pdfRequest.time = tijd?.toISOString();
        } else if (title.toLowerCase().includes('bevestiging indienen aangifte vennootschapsbelasting')) {
            pdfRequest.aangifteVpbJaar1 = JSON.parse(jsondata);
            pdfRequest.concept = false;
            pdfRequest.soort = PdfRequestSoort.Aangifte;
            pdfRequest.optie = PdfRequestOptie.GeheleRapport;
            pdfRequest.type = PdfRequestType.VPB;
            pdfRequest.time = tijd?.toISOString();
        } else if (title.toLowerCase().includes('bezwaarprodecure')) {
            const bezwaar = JSON.parse(jsondata) as Bezwaarschrift;
            if (bezwaar.bezwaarSpecificatie?.bijlagen) {
                bezwaar.bezwaarSpecificatie.bijlagen = bezwaar.bezwaarSpecificatie.bijlagen.map((bijlage) => ({
                    filename: bijlage.filename,
                    content: '',
                }));
                pdfRequest.bezwaarschrift = bezwaar;
            } else {
                pdfRequest.bezwaarschrift = bezwaar;
            }
            pdfRequest.concept = false;
            pdfRequest.conceptWatermerk = false;
            pdfRequest.soort = PdfRequestSoort.Bezwaar;
            pdfRequest.optie = PdfRequestOptie.GeheleRapport;
            pdfRequest.type = PdfRequestType.BZW;
            pdfRequest.time = tijd?.toISOString();
        } else if (title.toLowerCase().includes('bevestiging indienen opgave icp')) {
            pdfRequest.opgaafIcp = JSON.parse(jsondata);
            pdfRequest.concept = false;
            pdfRequest.conceptWatermerk = false;
            pdfRequest.soort = PdfRequestSoort.Opgaaf;
            pdfRequest.optie = PdfRequestOptie.GeheleRapport;
            pdfRequest.type = PdfRequestType.ICP;
            pdfRequest.time = tijd?.toISOString();
        } else if (title.toLowerCase().includes('aangifte vpb')) {
            pdfRequest.aangifteVpbJaar1 = JSON.parse(jsondata);
            pdfRequest.concept = false;
            pdfRequest.conceptWatermerk = false;
            pdfRequest.soort = PdfRequestSoort.Aangifte;
            pdfRequest.optie = PdfRequestOptie.GeheleRapport;
            pdfRequest.type = PdfRequestType.VPB;
            pdfRequest.time = tijd?.toISOString();
        } else if (title.toLowerCase().includes('aangifte ib')) {
            pdfRequest.aangifteJaar1 = JSON.parse(jsondata);
            pdfRequest.concept = false;
            pdfRequest.conceptWatermerk = false;
            pdfRequest.soort = PdfRequestSoort.Aangifte;
            pdfRequest.optie = PdfRequestOptie.GeheleRapport;
            pdfRequest.type = PdfRequestType.IB;
            pdfRequest.time = tijd?.toISOString();
        } else if (title.toLowerCase().includes('bevestiging indienen verzoek of wijziging voorlopige aanslag inkomstenbelasting')) {
            pdfRequest.aangifteJaar1 = JSON.parse(jsondata);
            pdfRequest.concept = false;
            pdfRequest.soort = PdfRequestSoort.Verzoek;
            pdfRequest.optie = PdfRequestOptie.Berekeningen;
            pdfRequest.type = PdfRequestType.IB;
            pdfRequest.time = tijd?.toISOString();
        } else if (title.toLowerCase().includes('bevestiging indienen verzoek of wijziging voorlopige aanslag vennootschapsbelasting')) {
            pdfRequest.aangifteVpbJaar1 = JSON.parse(jsondata);
            pdfRequest.concept = false;
            pdfRequest.soort = PdfRequestSoort.Verzoek;
            pdfRequest.optie = PdfRequestOptie.Berekeningen;
            pdfRequest.type = PdfRequestType.VPB;
            pdfRequest.time = tijd?.toISOString();
        } else if (title.toLowerCase().includes('bevestiging indienen aangifte dividendbelasting')) {
            pdfRequest.aangifteDivBel = JSON.parse(jsondata);
            pdfRequest.concept = false;
            pdfRequest.soort = PdfRequestSoort.Aangifte;
            pdfRequest.optie = PdfRequestOptie.GeheleRapport;
            pdfRequest.type = PdfRequestType.DB;
            pdfRequest.time = tijd?.toISOString();
        } else if (title.toLowerCase().includes('bevestiging indienen aangifte schenkbelasting')) {
            pdfRequest.aangifteSchenkBel = JSON.parse(jsondata);
            pdfRequest.concept = false;
            pdfRequest.soort = PdfRequestSoort.Aangifte;
            pdfRequest.optie = PdfRequestOptie.GeheleRapport;
            pdfRequest.type = PdfRequestType.SB;
            pdfRequest.time = tijd?.toISOString();
        } else if (title.toLowerCase().includes('vpb') && title.toLowerCase().includes('va')) {
            pdfRequest.aangifteVpbJaar1 = JSON.parse(jsondata);
            pdfRequest.concept = false;
            pdfRequest.conceptWatermerk = false;
            pdfRequest.soort = PdfRequestSoort.Verzoek;
            pdfRequest.optie = PdfRequestOptie.Berekeningen;
            pdfRequest.type = PdfRequestType.VPB;
            pdfRequest.time = tijd?.toISOString();
        } else if (title.toLowerCase().includes('ib') && title.toLowerCase().includes('va')) {
            pdfRequest.aangifteJaar1 = JSON.parse(jsondata);
            pdfRequest.concept = false;
            pdfRequest.conceptWatermerk = false;
            pdfRequest.soort = PdfRequestSoort.Verzoek;
            pdfRequest.optie = PdfRequestOptie.Berekeningen;
            pdfRequest.type = PdfRequestType.IB;
            pdfRequest.time = tijd?.toISOString();
        } else if (title.toLowerCase().includes('btw')) {
            pdfRequest.aangifteBtwJaar1 = JSON.parse(jsondata);
            pdfRequest.concept = false;
            pdfRequest.conceptWatermerk = false;
            pdfRequest.soort = PdfRequestSoort.Aangifte;
            pdfRequest.optie = PdfRequestOptie.GeheleRapport;
            pdfRequest.type = PdfRequestType.BTW;
            pdfRequest.time = tijd?.toISOString();
        } else if (title.toLowerCase().includes('tsl')) {
            pdfRequest.berekeningsJaar1 = JSON.parse(jsondata);
            pdfRequest.concept = false;
            pdfRequest.conceptWatermerk = false;
            pdfRequest.soort = PdfRequestSoort.Verzoek;
            pdfRequest.optie = PdfRequestOptie.Berekeningen;
            pdfRequest.type = PdfRequestType.TSL;
        } else if (title.toLowerCase().includes('toeslagen')) {
            pdfRequest.berekeningsJaar1 = JSON.parse(jsondata);
            pdfRequest.concept = false;
            pdfRequest.conceptWatermerk = false;
            pdfRequest.soort = PdfRequestSoort.Verzoek;
            pdfRequest.optie = PdfRequestOptie.Berekeningen;
            pdfRequest.type = PdfRequestType.TSL;
        } else if (title.toLowerCase().includes('verwerkte via')) {
            pdfRequest.aangifteJaar1 = JSON.parse(jsondata);
            pdfRequest.concept = false;
            pdfRequest.soort = PdfRequestSoort.Via;
            pdfRequest.optie = PdfRequestOptie.GeheleRapport;
            pdfRequest.type = PdfRequestType.IB;
            pdfRequest.time = tijd?.toISOString();
        } else if (title.toLowerCase().includes('loonheffingen')) {
            pdfRequest.loonAangifteDto = JSON.parse(jsondata);
            pdfRequest.concept = false;
            pdfRequest.soort = PdfRequestSoort.Aangifte;
            pdfRequest.optie = PdfRequestOptie.Loonaangifte;
            pdfRequest.type = PdfRequestType.LB;
            pdfRequest.time = tijd?.toISOString();
        } else {
            console.log(title);
            console.log(jsondata);
            console.log(tijd);
            this.toastService.showErrorMessage('Unsupported pdf');
        }
        return pdfRequest;
    }

    private isJsonObject(content: string): boolean {
        try {
            const parsed = JSON.parse(content);
            return parsed && typeof parsed === 'object' && !Array.isArray(parsed);
        } catch (error) {
            return false;
        }
    }

    private bepaalAangever(jsonData: string) {
        const aangifte = JSON.parse(jsonData) as Aangifte;
        if (aangifte.aangever.bsn === this.client.taxId) {
            return aangifte;
        } else {
            return this.convertAangifteToPartnerAangifte(aangifte);
        }
    }

    private convertAangifteToPartnerAangifte(aangifte: Aangifte) {
        if (aangifte.partnerAangifte) {
            const partnerAangifte = structuredClone(aangifte.partnerAangifte);
            partnerAangifte.partnerAangifte = structuredClone(aangifte);
            partnerAangifte.partnerAangifte.partnerAangifte = undefined;

            if (partnerAangifte.kinderen == null && aangifte.kinderen != null) {
                partnerAangifte.kinderen = [];
                for (const k of aangifte.kinderen) {
                    const partnerKind = {} as Kind;
                    partnerKind.voornaam = k.voornaam;
                    partnerKind.geboortedatum = k.geboortedatum;
                    partnerKind.bsn = k.bsn;
                    partnerKind.datumOverlijden = k.datumOverlijden;
                    partnerKind.ingeschrevenOpAdresAangever =
                        k.ingeschrevenOpAdresPartner != undefined ? k.ingeschrevenOpAdresPartner : false;
                    partnerKind.ingeschrevenOpAdresPartner = k.ingeschrevenOpAdresAangever;
                    partnerKind.bijExWoonAdres = k.bijExWoonAdres;
                    partnerKind.coOuderschap = k.coOuderschap;
                    partnerKind.tenMinsteDrieDagenPerWeekWoonachtig = k.tenMinsteDrieDagenPerWeekWoonachtig;
                    partnerKind.ouderOntvangtKinderbijslag = k.ouderOntvangtKinderbijslag;
                    partnerKind.onderhoudBelangrijkeMate = k.onderhoudBelangrijkeMate;
                    partnerAangifte.kinderen.push(partnerKind);
                }
            }

            if (
                partnerAangifte.box1 != null &&
                partnerAangifte.box1.belastbareInkomstenUitEigenWoning == null &&
                aangifte.box1 != null &&
                aangifte.box1.belastbareInkomstenUitEigenWoning != null
            ) {
                partnerAangifte.box1.belastbareInkomstenUitEigenWoning = aangifte.box1.belastbareInkomstenUitEigenWoning;
            }

            if (partnerAangifte.box2 == null && aangifte.box2 != null) {
                partnerAangifte.box2 = aangifte.box2;
                partnerAangifte.partnerAangifte.box2 = undefined;
            }

            if (partnerAangifte.box3 == null && aangifte.box3 != null) {
                partnerAangifte.box3 = aangifte.box3;
                partnerAangifte.partnerAangifte.box3 = undefined;
            }

            if (partnerAangifte.persoonsgebondenAftrek == null && aangifte.persoonsgebondenAftrek != null) {
                partnerAangifte.persoonsgebondenAftrek = aangifte.persoonsgebondenAftrek;
                partnerAangifte.partnerAangifte.persoonsgebondenAftrek = undefined;
            }

            const aangeverVoorheffingen = aangifte.voorheffingen;

            if (partnerAangifte.voorheffingen == null) {
                if (aangeverVoorheffingen != null && aangeverVoorheffingen.ingehoudenDividendbelasting != null) {
                    partnerAangifte.voorheffingen = {} as Voorheffingen;
                    partnerAangifte.voorheffingen.ingehoudenDividendbelasting = aangeverVoorheffingen.ingehoudenDividendbelasting;
                }

                if (aangeverVoorheffingen != null && aangeverVoorheffingen.buitenlandseBronheffing != null) {
                    if (partnerAangifte.voorheffingen == null) {
                        partnerAangifte.voorheffingen = {} as Voorheffingen;
                    }
                    partnerAangifte.voorheffingen.buitenlandseBronheffing = aangeverVoorheffingen.buitenlandseBronheffing;
                }
            } else {
                if (
                    partnerAangifte.voorheffingen.ingehoudenDividendbelasting == null &&
                    aangeverVoorheffingen != null &&
                    aangeverVoorheffingen.ingehoudenDividendbelasting != null
                ) {
                    partnerAangifte.voorheffingen.ingehoudenDividendbelasting = aangeverVoorheffingen.ingehoudenDividendbelasting;
                }

                if (
                    partnerAangifte.voorheffingen.buitenlandseBronheffing == null &&
                    aangeverVoorheffingen != null &&
                    aangeverVoorheffingen.buitenlandseBronheffing != null
                ) {
                    partnerAangifte.voorheffingen.buitenlandseBronheffing = aangeverVoorheffingen.buitenlandseBronheffing;
                }

                if (partnerAangifte.verdeling != null && aangifte.verdeling != null) {
                    partnerAangifte.verdeling = aangifte.partnerAangifte.verdeling;
                    partnerAangifte.partnerAangifte.verdeling = aangifte.verdeling;
                }
            }

            partnerAangifte.gezamenlijkeAangifte = aangifte.gezamenlijkeAangifte;
            partnerAangifte.aangever.burgerlijkeStaat = aangifte.aangever.burgerlijkeStaat;
            return partnerAangifte;
        }
        return aangifte;
    }
}
